import React, { useState, useEffect, useMemo, useRef } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import redirectURL from "../redirectURL";
import { RiDeleteBin5Line } from "react-icons/ri";
import { IoMdAdd } from "react-icons/io";

const PmWarehouseMaster = () => {
  const gridApi = useRef(null);
  const gridColumnApi = useRef(null);
  const [loadshow, setLoadshow] = useState("show-n");
  const [overlayBlock, setOverlayBlock] = useState("show-n");
  const [overlyBlock, setOverlyBlock] = useState("show-n");
  const [show, setShow] = useState(false);
  const [basicTitle, setBasicTitle] = useState("");
  const [basicType, setBasicType] = useState("default");
  const [rowData, setRowData] = useState([]);
  const [isSliderOpen, setSliderOpen] = useState(false);
  const [sliderType, setSliderType] = useState(null);
  const [sliderCl, setSliderCl] = useState("");
  const [sliderTitle, setSliderTitle] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  
  const [editFormData, setEditFormData] = useState({
    warehouse_id: "",
    warehouse_name: "",
    description: "",
    type: "",
    warehouse_address: "",
    region: ""
  });

  // Column Definitions
  const columnWithDefs = [
    {
      headerName: "",
      field: "actions",
      sortable: false,
      filter: false,
      width: "90px",
      cellClass: "custom-cell", 
      cellRendererFramework: (params) => (
        <div style={{
          height: "36px", 
          padding: 0, 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center"
        }}>
          <button
            title="Delete"
            style={{
              height: "30px",
              width: "60px",
              backgroundColor: "transparent",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => handleDelete(params.data)}
          >
            <RiDeleteBin5Line style={{ color: "red", fontSize: "20px" }} />
          </button>
        </div>
      ),
    },
    {
      headerName: "Warehouse ID",
      field: "warehouse_id",
      sortable: true,
      resizable: true,
      width: 130,
    },
    {
      headerName: "Warehouse Name",
      field: "warehouse_name",
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      resizable: true,
      width: 150,
    },
    {
      headerName: "Type",
      field: "type",
      sortable: true,
      resizable: true,
      width: 120,
    },
    {
      headerName: "Warehouse Address",
      field: "warehouse_address",
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      headerName: "Region",
      field: "region",
      sortable: true,
      resizable: true,
      width: 120,
    },
    {
      headerName: "Inserted Date",
      field: "inserted_date",
      sortable: true,
      resizable: true,
      width: 150,
      hide: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return '';
      }
    },
    {
      headerName: "Updated Date",
      field: "updated_date",
      sortable: true,
      resizable: true,
      width: 150,
      hide: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return '';
      }
    }
  ];

  // Reset Form Data
  const resetFormData = () => {
    setEditFormData({ 
      warehouse_id: "",
      warehouse_name: "",
      description: "",
      type: "",
      warehouse_address: "",
      region: ""
    });
  };

  // Hide Overlay and Slider
  const hideOverlySlideBlock = () => {
    setOverlyBlock("show-n");
    setLoadshow("show-n");
    setSliderOpen(false);
    resetFormData();
  };

  // Add New PM Warehouse
  const onClickAddNewPmWarehouse = () => {
    resetFormData();
    setSliderOpen(true);
    setSliderType(1);
    setSliderCl("slider-25");
    setSliderTitle("Add New PM Warehouse");
    setOverlyBlock("show-m");
  };

  // Handle Delete
  const handleDelete = (row) => {
    setSelectedRow(row);
    setShowDeletePopup(true);
    setOverlayBlock("show-m");
  };

  // Sidebar Configuration
  const sideBar = useMemo(() => ({
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
      },
      {
        id: "filters",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agFiltersToolPanel",
      },
    ]
  }), []);

  // Status Panel Configuration
  const statusPanel = {
    statusPanels: [
      {
        statusPanel: "agTotalAndFilteredRowCountComponent",
        align: "left"
      },
      {
        statusPanel: "agTotalRowCountComponent",
        align: "center"
      },
      { statusPanel: "agFilteredRowCountComponent" },
      { statusPanel: "agSelectedRowCountComponent" },
      { statusPanel: "agAggregationComponent" }
    ]
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
  };

  // Get PM Warehouse Data
  const getPmWarehouseData = async () => {
    try {
      setLoadshow("show-m");
      setOverlayBlock("show-m");
      
      const response = await redirectURL.post("masters/getPmWarehouseData", {});
      
      if (response.status === 200) {
        if (response.data.data && response.data.data.length > 0) {
          setRowData(response.data.data);
          setTimeout(() => autoSizeAllColumns(), 100);
        } else {
          setBasicTitle("No PM Warehouse data found");
          setBasicType("info");
          setShow(true);
        }
      }
    } catch (error) {
      console.error("Error fetching PM Warehouse data:", error);
      setBasicTitle("Failed to fetch PM Warehouse data");
      setBasicType("error");
      setShow(true);
    } finally {
      setLoadshow("show-n");
      setOverlayBlock("show-n");
    }
  };

  // Auto Size Columns
  const autoSizeAllColumns = () => {
    if (gridColumnApi.current) {
      const allColumns = gridColumnApi.current.getAllColumns();
      if (allColumns?.length) {
        const allColumnIds = allColumns.map((col) => col.getColId());
        gridColumnApi.current.autoSizeColumns(allColumnIds, false);
      }
    }
  };

  // Grid Ready
  const onGridReady = (params) => {
    gridApi.current = params.api;
    gridColumnApi.current = params.columnApi;
    setTimeout(() => autoSizeAllColumns(), 100);
  };

  // Close Alert
  const closeAlert = () => {
    setShow(false);
    setLoadshow("show-n");
    setOverlayBlock("show-n");
    setOverlyBlock("show-n");
    setShowDeletePopup(false);
    setSliderOpen(false);
    resetFormData();
  };

  // Close Slider
  const onClickingSliderCloseIcon = () => {
    setOverlyBlock("show-n");
    setSliderOpen(false);
    resetFormData();
  };

  // Handle Input Change
  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add New PM Warehouse
  const handleAddPmWarehouse = async () => {
    setLoadshow("show-m");
    setOverlayBlock("show-m");
    
    const params = {
      warehouse_id: editFormData.warehouse_id,
      warehouse_name: editFormData.warehouse_name,
      description: editFormData.description,
      type: editFormData.type,
      warehouse_address: editFormData.warehouse_address,
      region: editFormData.region
    };

    // Validation - All fields are mandatory
    const mandatoryFields = ['warehouse_id', 'warehouse_name', 'description', 'type', 'warehouse_address', 'region'];
    const validationErrors = mandatoryFields.filter(
      (key) => !params[key] || params[key].toString().trim() === ""
    );

    if (validationErrors.length > 0) {
      setShow(true);
      setBasicType("info");
      setBasicTitle(`Please fill in all mandatory fields`);
      setLoadshow("show-n");
      setOverlayBlock("show-n");
      return;
    }

    // Validate Warehouse ID format (alphanumeric)
    const warehouseIdRegex = /^[A-Za-z0-9]+$/;
    if (!warehouseIdRegex.test(params.warehouse_id)) {
      setShow(true);
      setBasicType("info");
      setBasicTitle("Warehouse ID should contain only letters and numbers");
      setLoadshow("show-n");
      setOverlayBlock("show-n");
      return;
    }

    try {
      const response = await redirectURL.post("masters/addPmWarehouse", params);
      
      if (response.status === 200 && response.data.status === "success") {
        await getPmWarehouseData();
        setLoadshow("show-n");
        setOverlayBlock("show-n");
        setShow(true);
        setBasicType("success");
        setBasicTitle("PM Warehouse Added Successfully");
        setSliderOpen(false);
        setOverlyBlock("show-n");
        resetFormData();
        
        // User Action Triggering
        const userData = {
          email: localStorage.getItem("email"),
          user_code: localStorage.getItem("user_code"),
          action: "New PM Warehouse Added",
          status: "Success",
          path: window.location.pathname
        };
        triggerUserAction(userData);
      } else {
        setLoadshow("show-n");
        setOverlayBlock("show-n");
        setShow(true);
        setBasicType("info");
        setBasicTitle(response.data.message || "Failed to add PM Warehouse");
        
        // User Action Triggering
        const userData = {
          email: localStorage.getItem("email"),
          user_code: localStorage.getItem("user_code"),
          action: "New PM Warehouse Added",
          status: "Failure",
          path: window.location.pathname
        };
        triggerUserAction(userData);
      }
    } catch (error) {
      const message = error.response?.data?.message || "An error occurred";
      setShow(true);
      setBasicType("info");
      setBasicTitle(message);
      setLoadshow("show-n");
      setOverlayBlock("show-n");
      
      // User Action Triggering
      const userData = {
        email: localStorage.getItem("email"),
        user_code: localStorage.getItem("user_code"),
        action: "New PM Warehouse Added",
        status: "Failure",
        path: window.location.pathname
      };
      triggerUserAction(userData);
    }
  };

  // Delete PM Warehouse
  const onClickingPopupDeleteBtn = async () => {
    setLoadshow("show-m");
    setOverlayBlock("show-m");
    
    try {
      const response = await redirectURL.post("masters/deletePmWarehouse", {
        id: selectedRow._id
      });
      
      if (response.data.status === "success") {
        await getPmWarehouseData();
        setLoadshow("show-n");
        setOverlayBlock("show-n");
        setShowDeletePopup(false);
        setShow(true);
        setBasicType("success");
        setBasicTitle("PM Warehouse Deleted Successfully");
        
        // User Action Triggering
        const userData = {
          email: localStorage.getItem("email"),
          user_code: localStorage.getItem("user_code"),
          action: "PM Warehouse Deleted",
          status: "Success",
          path: window.location.pathname
        };
        triggerUserAction(userData);
      } else {
        setLoadshow("show-n");
        setOverlayBlock("show-n");
        setShow(true);
        setBasicType("info");
        setBasicTitle("Failed to delete PM Warehouse");
        
        // User Action Triggering
        const userData = {
          email: localStorage.getItem("email"),
          user_code: localStorage.getItem("user_code"),
          action: "PM Warehouse Deleted",
          status: "Failure",
          path: window.location.pathname
        };
        triggerUserAction(userData);
      }
    } catch (error) {
      setLoadshow("show-n");
      setOverlayBlock("show-n");
      setShow(true);
      setBasicType("info");
      setBasicTitle("Failed to delete PM Warehouse");
      
      // User Action Triggering
      const userData = {
        email: localStorage.getItem("email"),
        user_code: localStorage.getItem("user_code"),
        action: "PM Warehouse Deleted",
        status: "Failure",
        path: window.location.pathname
      };
      triggerUserAction(userData);
    }
  };

  // Cancel Delete
  const onClickDeletePopupCancelBtn = () => {
    setOverlayBlock("show-n");
    setShowDeletePopup(false);
  };

  // Trigger User Action
  const triggerUserAction = (trigerdata) => {
    redirectURL.post('inventory/triggerUserAction', trigerdata);
  };

  // Initialize Data
  useEffect(() => {
    getPmWarehouseData();
  }, []);

  return (
    <>
      <div className="container-fluid mt-3">
        <div style={{ zIndex: "9999999" }}>
          <SweetAlert
            show={show}
            type={basicType}
            title={basicTitle}
            onConfirm={closeAlert}
            style={{ fontSize: "9px" }}
          />
        </div>

        <div className="row" style={{ fontSize: "1rem", fontWeight: "bold", fontFamily: "Arial, sans-serif" }}>
          <div className="col-sm-12">
            <h1 className="col-sm-12" style={{ color: "#B9366D", fontSize: "18px", fontWeight: "bold", fontFamily: "sans-serif" }}>
              PM Warehouse Master
            </h1>
          </div>
          
          {/* Add Button */}
          <div className="col-sm-12 mb-4" style={{ display: "flex", justifyContent: "flex-end" }}>
            <button 
              onClick={onClickAddNewPmWarehouse}
              style={{
                ...addButtonStyle,
                backgroundColor: "#93385D",
                color: "#fff",
                border: "2px solid #93385D",
              }}
            >
              <IoMdAdd style={{ color: "#fff", fontSize: "18px" }} />
              {" Add New"}
            </button>
          </div>

          {/* Data Grid */}
          <div className="col-sm-12 p-0 pl-2 mt-6">
            <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
              <AgGridReact
                rowData={rowData}
                columnDefs={columnWithDefs}
                defaultColDef={defaultColDef}
                domLayout="normal"
                sideBar={sideBar}
                statusBar={statusPanel}
                pagination={true}
                paginationPageSize={100}
                enableRangeSelection={true}
                gridOptions={{ suppressHorizontalScroll: false }}
                onGridReady={onGridReady}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Slider for Add */}
      <div className={`stock-status-slider ${isSliderOpen ? "open" : ""} ${sliderCl}`} style={{ width: "25%" }}>
        <div className="slider-header-container d-flex justify-content-between">
          <p className="m-0p">{sliderTitle}</p>
          <p className="m-0p" style={{ cursor: "pointer" }} onClick={onClickingSliderCloseIcon}>
            X&nbsp;&nbsp;&nbsp;
          </p>
        </div>

        {/* Add Form */}
        {sliderType === 1 && (
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            height: "100%", 
            overflow: "hidden" 
          }}>
            <form style={{ 
              flexGrow: 1, 
              overflowY: "auto", 
              padding: "20px",
              height: "100%"
            }}>
              <div style={{ paddingBottom: "20px" }}>
                {/* Warehouse ID */}
                <div className="form-group">
                  <label htmlFor="warehouse_id" style={labelStyle}>
                    Warehouse ID <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="warehouse_id"
                    value={editFormData.warehouse_id}
                    onChange={(e) => handleInputChange('warehouse_id', e.target.value.toUpperCase())}
                    placeholder="Enter Warehouse ID (e.g., WH02)"
                    style={inputStyle}
                    required
                    autoComplete="off"
                    maxLength={20}
                  />
                </div>

                {/* Warehouse Name */}
                <div className="form-group">
                  <label htmlFor="warehouse_name" style={labelStyle}>
                    Warehouse Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="warehouse_name"
                    value={editFormData.warehouse_name}
                    onChange={(e) => handleInputChange('warehouse_name', e.target.value)}
                    placeholder="Enter Warehouse Name"
                    style={inputStyle}
                    required
                    autoComplete="off"
                    maxLength={100}
                  />
                </div>

                {/* Description */}
                <div className="form-group">
                  <label htmlFor="description" style={labelStyle}>
                    Description <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="description"
                    value={editFormData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter Description"
                    style={inputStyle}
                    required
                    autoComplete="off"
                    maxLength={100}
                  />
                </div>

                {/* Type */}
                <div className="form-group">
                  <label htmlFor="type" style={labelStyle}>
                    Type <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    className="form-control"
                    id="type"
                    value={editFormData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    style={inputStyle}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="warehouse">Warehouse</option>
                  </select>
                </div>

                {/* Warehouse Address */}
                <div className="form-group">
                  <label htmlFor="warehouse_address" style={labelStyle}>
                    Warehouse Address <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    className="form-control"
                    id="warehouse_address"
                    value={editFormData.warehouse_address}
                    onChange={(e) => handleInputChange('warehouse_address', e.target.value)}
                    placeholder="Enter Warehouse Address"
                    style={{...inputStyle, height: "80px"}}
                    required
                    autoComplete="off"
                    maxLength={200}
                    rows={3}
                  />
                </div>

                {/* Region */}
                <div className="form-group">
                  <label htmlFor="region" style={labelStyle}>
                    Region <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    className="form-control"
                    id="region"
                    value={editFormData.region}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                    style={inputStyle}
                    required
                  >
                    <option value="">Select Region</option>
                    <option value="North">North</option>
                    <option value="South">South</option>
                    <option value="East">East</option>
                    <option value="West">West</option>
                  </select>
                </div>

                {/* Submit Button */}
                <div style={submitButtonContainerStyle}>
                  <button
                    type="button"
                    onClick={handleAddPmWarehouse}
                    style={submitButtonStyle}
                  >
                    ADD
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      {showDeletePopup && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={deletePopupStyle}
        >
          <div className="inventory-delete-popup">
            <p style={deletePopupTextStyle}>
              {`Are you sure you want to delete ${selectedRow ? selectedRow.warehouse_name : ""}?`}
            </p>
            <div className="d-flex justify-content-center align-items-center">
              <button className="btn btn-danger" onClick={onClickingPopupDeleteBtn}>
                Delete
              </button>
              <button className="btn btn-secondary" onClick={onClickDeletePopupCancelBtn}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loader and Overlays */}
      <div className={`loader ${loadshow}`} style={{ zIndex: "1002" }}></div>
      <div className={`overlay-block ${overlayBlock}`}></div>
      <div className={"overly-block " + overlyBlock} onClick={hideOverlySlideBlock}></div>
    </>
  );
};

// Styles
const addButtonStyle = {
  padding: "5px 20px",
  backgroundColor: "#93385D",
  color: "#fff",
  fontFamily: "sans-serif",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  fontSize: "0.8rem",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  gap: "5px"
};

const labelStyle = {
  fontSize: "0.9rem",
  fontWeight: "bold",
  color: "#333",
  marginBottom: "5px",
  display: "block"
};

const inputStyle = {
  width: "100%",
  borderRadius: "5px",
  border: "1px solid #ccc",
  fontSize: "0.9rem"
};

const submitButtonContainerStyle = {
  textAlign: "right",
  marginTop: "20px",
  paddingTop: "10px",
  borderTop: "1px solid #eee"
};

const submitButtonStyle = {
  backgroundColor: "#93385D",
  color: "#fff",
  padding: "10px 30px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  fontSize: "0.9rem",
  fontWeight: "bold"
};

const deletePopupStyle = {
  position: "fixed",
  top: "40%",
  left: "40%",
  zIndex: "1003",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  padding: "20px",
  borderRadius: "10px",
};

const deletePopupTextStyle = {
  color: "grey",
  fontSize: "19px",
  textAlign: "center",
  fontWeight: "bold",
  marginBottom: "20px",
};

export default PmWarehouseMaster;